import React, { useEffect, useState, useRef } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { array } from 'prop-types';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { process as kendoProcess } from '@progress/kendo-data-query';
import { kendoGridInlineStyling } from '../../../helpers/kendoGridInlineStyleHelper';
import BobLoadingMask from '../../../components/BobLoadingMask';
import useWindowSize from '../../../hooks/useWindowSize';
import { dashboardContent } from '../../../localization/en-us/dashboardContent';
import {calculateMinColumnWidths} from '../../../helpers/calculateMinColumnWidths';

import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';

import classes from '../SitePage/Table.module.css';

const initialDataState = {
    sort: [],
    take: 10,
    skip: 0
};

const EquipmentPanel = ({
    activeNavCompany,
    bobLoading,
    companies,
    fetchDashboardData,
    fetchNavClients,
    systemEquipment,
    systemEquipmentMeta,
    fetchSingleEquipment,
    history,
    resetNavOptions,
    refreshTab,
    setRefreshTab,
    searchTerm,
    setNavCompany,
    targetNavCompany,
    workbookModeEnabled,
    ...props
}) => {
    const [take, setTake] = useState(10);
    const [pageable] = useState({ pageSizes: true });
    const [dataState, setDataState] = useState(initialDataState);
    const [equipmentColumnWidth, setEquipmentColumnWidth] = useState(250);

    const queryValues = queryString.parse(history.location.search);
    const companyId = queryValues.company;
    const clientId = queryValues.client;
    const siteId = queryValues.site;
    const systemId = queryValues.system;

    const gridDivRef = useRef(null);
    const gridRef = useRef(null);
    const windowSize = useWindowSize();

    useEffect(() => {
        const columns = columnList.map((c) => {
            c.width = c.field === 'equipmentName' ? 250 : c.width;
            return c;
        });
        const colWidth = calculateMinColumnWidths(columns.filter(c => c.show), gridDivRef.current.offsetWidth, 'equipmentName', 250, true);
        setEquipmentColumnWidth(colWidth);
        columns.find(c => c.id === 'equipmentColumn').width = colWidth;
        setColumnList(columns);
    }, [windowSize]);

    useEffect(() => {
        const refresh = async () => {
            await fetchDashboardData(companyId, 'SystemEquipment', { clientId, siteId, systemId, sort: 'equipmentName^asc'});

            setRefreshTab('');
        };
        if (refreshTab) refresh();
    }, [refreshTab]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            initialDataState.take = take;
            setDataState(initialDataState);
        }
        setColumnList(columnList.map(c => {
            c.filterable = c.actionCell ? false : workbookModeEnabled;
            return c;
        }));    
    }, [workbookModeEnabled]);
    
    const renderNavUrl = dataItem => {
        const queryValues = queryString.parse(history.location.search);

        return `equipment?client=${dataItem.clientId}&company=${queryValues.company}&equipment=${dataItem.equipmentId}&site=${dataItem.siteId}`;
    };

    const handleNav = async dataItem => {
        let path = '/v2/dashboard/equipment';

        const queryValues = queryString.parse(history.location.search);
        queryValues.client = dataItem.clientId;
        queryValues.site = dataItem.siteId;
        queryValues.equipment = dataItem.equipmentId;

        if (targetNavCompany.id !== dataItem.companyId) {
            resetNavOptions('all');
            const company = companies.find(item => item.company.id === dataItem.companyId);
            setNavCompany({
                name: company.company.name,
                id:   company.company.id
            });
            fetchNavClients(dataItem.companyId);
        }

        const queryValuesString = queryString.stringify(queryValues);

        history.push(`${path}?${queryValuesString}`);

        const options = {extendedData: true};
        await fetchSingleEquipment(queryValues, options);
    };

    const nameCell = props => {
        return (
            <td>
                <Link
                    to={renderNavUrl(props.dataItem)}
                    onClick={
                        (e) => {
                            if (!e.ctrlKey) {
                                handleNav(props.dataItem);
                            }
                        }
                    }
                >
                    {props.dataItem[props.field]}
                </Link>
            </td>
        );
    };

    const priorityCell = props => {
        let imgPath = '/assets/images/low.svg';
        if (props.field === 'mediumFaultCount') {
            imgPath = '/assets/images/medium.svg';
        } else if (props.field === 'highFaultCount') {
            imgPath = '/assets/images/high.svg';
        }
        return (
            <td>
                <img className={ props.dataItem[props.field] > 0 ? classes.IconImage: classes.IconImageGray} src={imgPath} alt="" />
                {props.dataItem[props.field]}
            </td>
        );
    };

    const scoresCell = props => {
        let value = props.dataItem[props.field];
        value = value.toString().includes('.') ? parseFloat(value).toFixed(2) : value;

        return (
            <td>
                {value + '%'}
            </td>
        );
    };

    const onDataStateChange = event => {
        setTake(event.dataState.take);
        setDataState(event.dataState);
    };

    const defaultColumnList = [
        {
            id:          'equipmentColumn',
            field:       'equipmentName',
            title:       dashboardContent.equipment,
            width:       250,
            cell:        nameCell,
            filterable:  workbookModeEnabled,
            sortable:    true,
            reorderable: true,
            filter:      'text',
            locked:      false,
            show:        true
        },
        {
            field:       'equipmentTypeName',
            title:       dashboardContent.equipmentType,
            width:       250,
            filterable:  workbookModeEnabled,
            filter:      'text',
            sortable:    true,
            reorderable: true,
            locked:      false,
            show:        true
        },
        {
            field:       'sensorCount',
            title:       dashboardContent.sensors,
            width:       200,
            filter:      'numeric',
            filterable:  workbookModeEnabled,
            sortable:    true,
            reorderable: true,
            locked:      false,
            show:        true
        },
        {
            field:       'equipmentScore',
            title:       dashboardContent.score,
            width:       200,
            cell:        scoresCell,
            filter:      'numeric',
            filterable:  workbookModeEnabled,
            sortable:    true,
            reorderable: true,
            locked:      false,
            show:        true
        },
        {
            field:       'totalFaultCount',
            title:       dashboardContent.faults,
            width:       200,
            filter:      'numeric',
            filterable:  workbookModeEnabled,
            sortable:    true,
            reorderable: true,
            locked:      false,
            show:        true
        },
        {
            field:       'highFaultCount',
            title:       dashboardContent.high,
            width:       200,
            cell:        priorityCell,
            filter:      'numeric',
            filterable:  workbookModeEnabled,
            sortable:    true,
            reorderable: true,
            locked:      false,
            show:        true
        },
        {
            field:       'mediumFaultCount',
            title:       dashboardContent.medium,
            width:       200,
            cell:        priorityCell,
            filter:      'numeric',
            filterable:  workbookModeEnabled,
            sortable:    true,
            reorderable: true,
            locked:      false,
            show:        true
        },
        {
            field:       'lowFaultCount',
            title:       dashboardContent.low,
            width:       200,
            cell:        priorityCell,
            filter:      'numeric',
            filterable:  workbookModeEnabled,
            sortable:    true,
            reorderable: true,
            locked:      false,
            show:        true
        }
    ];
    const [columnList, setColumnList] = useState(defaultColumnList);

    return (
        <>
            <div ref={gridDivRef} className={classes.Table}>
                <style>
                    {kendoGridInlineStyling}
                </style>
                <Grid
                    ref={gridRef}
                    sortable={{
                        mode: 'multiple'
                    }}
                    className={classes.KendoGrid}
                    data={kendoProcess(systemEquipment, dataState)}
                    {...dataState}
                    pageable={pageable}
                    reorderable
                    onDataStateChange={onDataStateChange}
                    filterable={workbookModeEnabled}
                >
                    {
                        columnList.map(
                            (column, index) => 
                                column.show && (
                                    <Column
                                        key={index}
                                        field={column.field}
                                        title={column.title}
                                        id={column.id}
                                        cell={column.cell}
                                        width={column.id === 'equipmentColumn' ? equipmentColumnWidth : column.width}
                                        filter={column.filter}
                                        sortable={column.sortable}
                                        filterable={column.filterable}
                                        locked={column.locked}
                                        reorderable={column.reorderable}
                                    />
                                )
                        )
                    }                
                </Grid>
                {
                    bobLoading.includes('SystemEquipment') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </>
    );
};

EquipmentPanel.defaultProps = {
    equipment: []
};

EquipmentPanel.propTypes = {
    equipment: array
};

const mapStateToProps = state => ({
    activeNavCompany:    state.bob.activeNavCompany,
    bobLoading:          state.bob.bobLoading,
    companies:           state.core.companies,
    systemEquipment:     state.dashboard.systemEquipment,
    systemEquipmentMeta: state.dashboard.systemEquipmentMeta,
    targetNavCompany:    state.bob.targetNavCompany,
    workbookModeEnabled: state.dashboard.workbookModeEnabled
});

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EquipmentPanel));
